import { useCallback } from 'react';
import { api } from 'src/assets/static/ApiRoutes';
import useAsync from 'src/hooks/useAsync';
import axiosInstance from 'src/utils/axios';
import { formatOcrResponse } from 'src/utils/transformation';

export type OcrField = {
  value: string | null;
  pageNumber: string | null;
  boundingBox: number[] | null;
  confidence: string | null;
  isHighlight?: boolean;
};

export type OcrItem = {
  itemNumber: OcrField;
  itemDescription: OcrField;
  itemQuantity: OcrField;
  itemPrice: OcrField;
  amount: OcrField;
  uom: OcrField;
  taxableIndicator: OcrField;
  itemSequenceNumber: OcrField;
  priceCurrency: OcrField;
  amountCurrency: OcrField;
  itemConfidence: string;
  recvReceiptLineItemId?: number;
};

export type OcrInvoiceResponse = {
  documentInstanceId: string;
  fileName: string;
  createdDate: string;
  fileType: string;
  errorMessage: string | null;
  invoiceNumber: OcrField;
  invoiceDate: OcrField;
  customerId: OcrField;
  customerName: OcrField;
  customerAddress: OcrField;
  customerAddressRecipient: OcrField;
  billingAddress: OcrField;
  shippingAddress: OcrField;
  supplierName: OcrField;
  supplierAddress: OcrField;
  amountDue: OcrField;
  invoiceAmountCurrency: OcrField;
  subTotal: OcrField;
  totalTax: OcrField;
  totalDiscount: OcrField;
  dueDate: OcrField;
  purchaseOrder: OcrField;
  totalShipping: OcrField;
  items: { item: OcrItem }[];
  itemsCount: number;
};

export interface OcrResponse {
  invoice: OcrInvoiceResponse;
  confidence: string;
}

export type OcrFieldKeys = {
  [K in keyof OcrInvoiceResponse]: OcrInvoiceResponse[K] extends OcrField ? K : never;
}[keyof OcrInvoiceResponse];

export type OcrLineItemsKeys = Exclude<
  {
    [K in keyof OcrItem]: OcrItem[K] extends OcrField ? K : never;
  }[keyof OcrItem],
  undefined
>;

const useGetOcrResponseApi = () => {
  const call = useCallback(async (attachmentId?: number) => {
    try {
      const res = await axiosInstance.get<OcrResponse>(api.getOcrResponse(Number(attachmentId)));
      return formatOcrResponse(res.data.invoice);
    } catch (error) {
      throw new Error(error);
    }
  }, []);

  const { value, setValue, execute, isLoading } = useAsync(
    call,
    false,
    undefined,
    {} as OcrInvoiceResponse
  );

  return {
    getOcrResponse: execute,
    ocrResponse: value,
    setOcrResponse: setValue,
    isOcrResponseLoading: isLoading,
  };
};

export default useGetOcrResponseApi;
